import React, { useState, useEffect } from "react";
import AuthLayout from "./AuthLayout";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, IconButton, InputAdornment, TextField } from "@mui/material";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useResetPasswordMutation } from "../../store/api/authAPI";
import { showToast, showError } from "../../constants/toasts";

interface FormErrors {
  newPassword?: string;
  confirmPassword?: string;
}

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useSelector((state: RootState) => state.auth);
  const [resetPasswordMutation, { isLoading: isResetting }] =
    useResetPasswordMutation();

  const [passwordVisibility, setPasswordVisibility] = useState({
    new: false,
    confirm: false,
  });

  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [email, setEmail] = useState<string>("");

  // Get email from navigation state
  useEffect(() => {
    const stateEmail = (location.state as any)?.email;
    if (stateEmail) {
      setEmail(stateEmail);
    } else {
      showError("Email not found. Please try again.");
      navigate("/forgot-password");
    }
  }, [location, navigate]);

  // Check if token exists (from OTP verification)
  // useEffect(() => {
  //   if (!token) {
  //     showError("Session expired. Please verify OTP again.");
  //     navigate("/otp", { state: { email } });
  //   }
  // }, [token, navigate, email]);

  const toggleVisibility = (field: "new" | "confirm") => {
    setPasswordVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  // Password validation
  const validatePassword = (password: string): boolean => {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special character
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^()_+\-=\[\]{};':"\\|,.<>\/])[A-Za-z\d@$!%*?&#^()_+\-=\[\]{};':"\\|,.<>\/]{8,}$/;
    return passwordRegex.test(password);
  };

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // New password validation
    if (!formData.newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters";
    } else if (!validatePassword(formData.newPassword)) {
      newErrors.newPassword =
        "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character";
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      showError("Please fix the errors in the form");
      return;
    }

    if (!token) {
      showError("Session expired. Please verify OTP again.");
      navigate("/otp", { state: { email } });
      return;
    }

    try {
      const response = await resetPasswordMutation({
        isResetPassword: true,
        newPassword: formData.newPassword,
      }).unwrap();

      if (response) {
        showToast(response.message || "Password reset successfully!");
        // Navigate to login page after successful password reset
        setTimeout(() => {
          navigate("/");
        }, 1500);
      }
    } catch (error: any) {
      // Handle error response
      let errorMessage = "Failed to reset password. Please try again.";

      // Handle PARSING_ERROR (when server returns HTML instead of JSON)
      if (
        error?.status === "PARSING_ERROR" ||
        error?.error?.status === "PARSING_ERROR"
      ) {
        const originalStatus =
          error?.originalStatus || error?.error?.originalStatus;
        if (originalStatus === 404) {
          errorMessage = "Endpoint not found. Please check the API configuration.";
        } else {
          errorMessage =
            "Server returned an invalid response. Please check the API endpoint.";
        }
        console.error("PARSING_ERROR details:", error);
      } else {
        // Try to extract error message from various error formats
        errorMessage =
          error?.data?.message ||
          error?.data?.error ||
          error?.error?.data?.message ||
          error?.error?.data?.error ||
          error?.message ||
          errorMessage;
      }

      showError(errorMessage);
    }
  };

  return (
    <AuthLayout>
      <div className="authBox">
        <h2>Reset Password</h2>
        <p>Create a secure password to protect your account.</p>
        <form className="form" onSubmit={handleSubmit}>
          <div className="control_group">
            <label htmlFor="new-password">New Password</label>
            <TextField
              id="new-password"
              name="newPassword"
              placeholder="Enter New Password"
              fullWidth
              hiddenLabel
              type={passwordVisibility.new ? "text" : "password"}
              value={formData.newPassword}
              onChange={handleChange}
              error={!!errors.newPassword}
              helperText={errors.newPassword || " "}
              FormHelperTextProps={{
                style: { margin: 0, minHeight: errors.newPassword ? "auto" : "20px" }
              }}
              disabled={isResetting}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" className="eye_btn">
                    <IconButton
                      aria-label={
                        passwordVisibility.new ? "Hide password" : "Show password"
                      }
                      onClick={() => toggleVisibility("new")}
                      onMouseDown={handleMouseDown}
                      edge="end"
                      disabled={isResetting}
                    >
                      {passwordVisibility.new ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="control_group">
            <label htmlFor="confirm-password">Confirm Password</label>
            <TextField
              id="confirm-password"
              name="confirmPassword"
              placeholder="Enter Confirm Password"
              fullWidth
              hiddenLabel
              type={passwordVisibility.confirm ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={handleChange}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword || " "}
              FormHelperTextProps={{
                style: { margin: 0, minHeight: errors.confirmPassword ? "auto" : "20px" }
              }}
              disabled={isResetting}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" className="eye_btn">
                    <IconButton
                      aria-label={
                        passwordVisibility.confirm
                          ? "Hide password"
                          : "Show password"
                      }
                      onClick={() => toggleVisibility("confirm")}
                      onMouseDown={handleMouseDown}
                      edge="end"
                      disabled={isResetting}
                    >
                      {passwordVisibility.confirm ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="form_btn">
            <Button
              className="btn btn_primary w_100"
              type="submit"
              variant="contained"
              disabled={isResetting}
            >
              {isResetting ? "Resetting..." : "Submit"}
            </Button>
          </div>
        </form>

        <div className="auth_bottom">
          <p>
            <button
              type="button"
              className="link"
              onClick={() => navigate("/")}
              disabled={isResetting}
            >
              <KeyboardBackspaceIcon /> Back to Login
            </button>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default ResetPassword;
